import React, { ReactNode, useState } from 'react';
import { FieldArrayRenderProps, FormikProps } from 'formik';
import { Button, Modal } from 'antd';
import cx from 'classnames';
import { FormikTouched } from 'formik/dist/types';

import FinancialRatesSection from './FinancialRatesSection';
import { FinancialInfoFormValues } from '../../FinancialInfo';
import { AssignmentMultiRateEditModel } from 'api/backofficeApiClient';
import { isAddButtonVisible } from 'utils/fieldArrayUtils';
import { MultiRateSchedulerEditModel } from 'api/adminApiClient';

import styles from '../../FinancialInfo.module.less';

type Props = FieldArrayRenderProps & {
  children: ReactNode;
  renderChangesEffectiveDateBtn: () => React.ReactNode;
  onRemoveMultiRateScheduler: (index: number) => void;
  predefinedMultiRates?: MultiRateSchedulerEditModel[];
};

const defaultValue: AssignmentMultiRateEditModel = {
  payType: undefined,
  payRate: undefined,
  billRate: undefined,
  specialInstuctions: undefined,
};

const MAX_NUMBER_OF_TABS = 20;
const firstSectionIndex = 0;

export default function FinancialRatesSectionArray(props: Props) {
  const {
    form,
    children,
    renderChangesEffectiveDateBtn,
    onRemoveMultiRateScheduler,
    predefinedMultiRates,
  } = props;
  const { values, touched } = form as FormikProps<FinancialInfoFormValues>;
  const [viewSectionIndex, setViewSectionIndex] =
    useState<number>(firstSectionIndex);
  const multiRates = values.multiRates;
  const numberOfTabs = Array.from({
    length: (multiRates?.length || 0) + 1,
  });

  const handleSwitchSection = (index: number) => {
    setViewSectionIndex(index);
  };

  const onRemove = (index: number) => {
    props.remove(index);
    onRemoveMultiRateScheduler(index);
    handleSwitchSection(index);
  };

  const handleMultipleRateRemove = (index: number) => {
    const multiRatesTouched = touched.multiRates as FormikTouched<
      AssignmentMultiRateEditModel[] | undefined
    >;
    const multiRateTouched = multiRatesTouched && multiRatesTouched[index];
    const currentValue = (values.multiRates || [])[index];
    const hasValues = Object.values(currentValue).filter(Boolean).length > 0;

    if (!multiRateTouched && !hasValues) {
      return onRemove(index);
    }

    Modal.confirm({
      title:
        'Please confirm the removal of additional rate. All data will be removed. Are you sure?',
      icon: <span className="icon icon-attention-filled" />,
      onOk() {
        onRemove(index);
      },
      onCancel() {},
    });
  };

  return (
    <>
      <div className={styles.titleBox}>
        <div className="modal-title">Financial information</div>
        <div className={styles.navBox}>
          {numberOfTabs.map((numberOfTab, index) => (
            <div key={index} className={styles.navBoxItem}>
              <Button
                onClick={() => handleSwitchSection(index)}
                className={cx('round-btn', {
                  'is-active': index === viewSectionIndex,
                })}
              >
                {index + 1}
              </Button>

              {isAddButtonVisible(numberOfTabs, index, MAX_NUMBER_OF_TABS) && (
                <Button
                  onClick={() => {
                    props.push(defaultValue);
                    handleSwitchSection(numberOfTabs.length);
                  }}
                  className="round-btn"
                >
                  +
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
      {viewSectionIndex === firstSectionIndex && children}
      {viewSectionIndex !== firstSectionIndex &&
        multiRates?.length &&
        multiRates.map((value: AssignmentMultiRateEditModel, index: number) => {
          if (viewSectionIndex !== index + 1) return null;

          return (
            <div key={index}>
              <FinancialRatesSection
                index={index}
                formNamePrefix={props.name}
                renderChangesEffectiveDateBtn={renderChangesEffectiveDateBtn}
                predefinedMultiRates={predefinedMultiRates}
                actionButton={
                  <>
                    <Button
                      onClick={() => {
                        handleMultipleRateRemove(index);
                      }}
                      type="primary"
                    >
                      Remove additional rate
                    </Button>
                  </>
                }
              />
            </div>
          );
        })}
    </>
  );
}
